import { FC, useState } from "react";
import ModalContainer from "../UIElements/modal-container";
import {
  Box,
  Button,
  InputLabel,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SelectInput from "../UIElements/select";
import { LoadingButton } from "@mui/lab";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationService } from "../notification";
import { useMutation } from "@tanstack/react-query";
import {
  apiCreateEmployeeAppraisal,
  apiUpdateEmploymentStatus,
} from "../../api/employment";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { PATH_EMPLOYMENT } from "../../routes/path";
import { InputCustom, TextArea } from "../UIElements/input-ui";
import { labelSty } from "../UIElements/styles-input";
import { CustomInput } from "../UIElements/input-verification";
import HoverRating from "../UIElements/rating";
import { CustomSelectInput } from "../UIElements/custom-select";
import { strengthsData } from "./data";
import SelectInputMultiple from "../UIElements/select-multiple";
import { validateEmploeeComment } from "../../utils/validations";

export const EmploymentUpdateModal: FC<{
  open: boolean;
  status: string;
  employeeId: string;
  handleModal: () => void;
  modalStatus: string;
  employmentId: string;
  setModalStatus: React.Dispatch<React.SetStateAction<string>>;
}> = ({
  open,
  handleModal,
  employeeId,
  status,
  modalStatus,
  employmentId,
  setModalStatus,
}) => {
  const { token } = useAuth();
  const [reason, setReason] = useState("");
  const { showNotification } = useNotificationService();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (fieldValues: {
      status: string;
      reasons: string;
      employmentId: string;
      token: string;
    }) => apiUpdateEmploymentStatus(fieldValues),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        showNotification(data?.data?.message ?? "Status Updated");
        navigate(PATH_EMPLOYMENT.root);
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });
  const handleStatus = () => {
    const form = {
      status: modalStatus,
      reasons: reason,
      employmentId: employmentId,
      token,
    };
    mutation.mutate(form);
  };
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"xs"}
      openModal={open}
      fullWidth={true}
      handleModal={handleModal}
    >
      <Box
        sx={{
          background: "#fff",
          padding: 3,
          borderRadius: "16px",
        }}
      >
        <Stack>
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 500,
              color: "#292925",
              marginBottom: 3,
            }}
          >
            Update Employment Status
          </Typography>
          <SelectInput
            label={"Select Status"}
            value={modalStatus}
            onChange={(e) => setModalStatus(e.target.value)}
            options={[
              { value: "inactive", label: "Inactive" },
              { value: "active", label: "Active" },
            ]}
          />

          <SelectInput
            label={"Select Reason"}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            options={[
              { value: "", label: "Select Reason" },
              { value: "terminated", label: "Terminated" },
              { value: "suspended", label: "Suspended" },
              { value: "retirement", label: "Retirement" },
              { value: "dissatisfaction", label: "Dissatisfaction" },
              { value: "better opportunities", label: "Better opportunities" },
            ]}
          />
          <Stack
            direction="row"
            spacing={3}
            justifyContent={"flex-end"}
            sx={{ mt: 3 }}
          >
            <Button
              variant="outlined"
              onClick={handleModal}
              sx={{ borderRadius: "8px" }}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              color="primary"
              loading={mutation.isPending}
              onClick={handleStatus}
              sx={{ borderRadius: "6px" }}
            >
              Update
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </ModalContainer>
  );
};

interface ModalContainerPropTypes {
  open: boolean;
  employeeId: string;
  employmentId: string;
  modalHandler: () => void;
  handleRefresh: () => void;
}

export const EmploymentCommentModal = ({
  open,
  employeeId,
  employmentId,
  modalHandler,
  handleRefresh,
}: ModalContainerPropTypes) => {
  const theme = useTheme();
  const { showNotification } = useNotificationService();
  const [feedbackstate, setFeedbackstate] = useState({
    performance_rating: 2,
    comments: "",
    goals: "",
    overall_summary: "",
    appraisal_type: "",
  });
  const [strengths, setStrengths] = useState<string[]>([]);

  const handleStrength = (event: SelectChangeEvent<typeof strengths>) => {
    const {
      target: { value },
    } = event;
    setStrengths(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value,
    );
  };

  const mutation = useMutation({
    mutationFn: (fieldValues: {
      employee_id: string;
      performance_rating: number;
      comment: string;
      goals: string;
      overall_summary: string;
      appraisal_type: string;
    }) => apiCreateEmployeeAppraisal(fieldValues, strengths),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if (data?.status === 422) {
        showNotification("An error occurred", "error");
        return;
      }

      if ("data" in data) {
        showNotification(data?.data?.message ?? "");
        handleRefresh();
        setFeedbackstate({
          performance_rating: 2,
          comments: "",
          goals: "",
          overall_summary: "",
          appraisal_type: "",
        });
        setStrengths([]);
        modalHandler();
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });

  const handleRating = (val: number) => {
    setFeedbackstate((prev) => ({ ...prev, performance_rating: val }));
  };
  const handleStatus = (e) => {
    e.preventDefault();
    const form = {
      employee_id: employmentId,
      performance_rating: feedbackstate.performance_rating,
      comment: feedbackstate.comments,
      goals: feedbackstate.goals,
      overall_summary: feedbackstate.overall_summary,
      appraisal_type: feedbackstate.appraisal_type,
    };

    const check_value = validateEmploeeComment(
      form,
      strengths,
      showNotification,
    );
    if (check_value) {
      mutation.mutate(form);
    }
  };
  return (
    <ModalContainer
      sx={{
        borderRadius: "16px",
      }}
      size={"sm"}
      openModal={open}
      fullWidth={true}
      handleModal={() => {}}
    >
      <Box sx={{ background: "#fff", padding: 3, borderRadius: "16px" }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Outfit",
            fontWeight: 500,
            color: "#292925",
          }}
        >
          Feedback and Comments
        </Typography>
        <Box component={"form"} onSubmit={handleStatus}>
          <Stack sx={{ my: 3 }}>
            <SelectInputMultiple
              label={"Strengths"}
              value={strengths}
              disabled={false}
              onChange={handleStrength}
              options={strengthsData}
            />
          </Stack>
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Performance Rating </InputLabel>
            <HoverRating handleRating={handleRating} />
          </Stack>
          <Stack sx={{ my: 3 }}>
            <SelectInput
              label={"Select Appraisal Type"}
              value={feedbackstate.appraisal_type}
              onChange={(e) =>
                setFeedbackstate((prev) => ({
                  ...prev,
                  appraisal_type: e.target.value,
                }))
              }
              options={[
                { value: "monthly", label: "Monthly" },
                { value: "quarterly", label: "Quarterly" },
                { value: "annually", label: "Annually" },
              ]}
            />
          </Stack>
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Comments </InputLabel>
            <TextArea
              placeholder={"Enter text here."}
              value={feedbackstate.comments}
              onchange={(e) =>
                setFeedbackstate((prev) => ({
                  ...prev,
                  comments: e.target.value,
                }))
              }
            />
          </Stack>
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Goals </InputLabel>
            <TextArea
              placeholder={"Enter text here."}
              value={feedbackstate.goals}
              onchange={(e) =>
                setFeedbackstate((prev) => ({ ...prev, goals: e.target.value }))
              }
            />
          </Stack>
          <Stack sx={{ my: 3 }}>
            <InputLabel sx={{ ...labelSty }}>Overall Summary </InputLabel>
            <TextArea
              placeholder={"Enter text here."}
              value={feedbackstate.overall_summary}
              onchange={(e) =>
                setFeedbackstate((prev) => ({
                  ...prev,
                  overall_summary: e.target.value,
                }))
              }
            />
          </Stack>
          <Stack
            direction={"row"}
            justifyContent={"space-around"}
            sx={{ gap: 3 }}
          >
            <Button
              onClick={modalHandler}
              sx={{
                width: 150,
                borderRadius: "12px",
                background: theme.palette.lightBlue.light,
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              loading={mutation.isPending}
              sx={{ width: 150, borderRadius: "12px", textTransform: "none" }}
              variant="contained"
            >
              Done
            </LoadingButton>
          </Stack>
        </Box>
      </Box>
    </ModalContainer>
  );
};
